import axios from 'axios';
import { getMarketplaceApiBaseUrl } from './resolveApiBaseUrl';
import api from './itemsApiClient';

const API_BASE_URL = process.env.REACT_APP_ITEMS_API_URL ||
  getMarketplaceApiBaseUrl() || 'http://localhost:8080/api';

export const boutiqueItemsAPI = {
  buildShopRef: (shopOrId) => {
    const id = typeof shopOrId === 'object' ? shopOrId?.id : shopOrId;
    return `BSH-${id}`;
  },

  getBoutiqueItemsPage: async (filters = {}, page = 1, pageSize = 24) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    const response = await axios.get(`${API_BASE_URL}/public/boutique-items/?${params.toString()}`);
    const data = response.data;
    if (Array.isArray(data)) {
      return {
        results: data,
        count: data.length,
        page: 1,
        page_size: data.length || pageSize,
        total_pages: 1,
        has_next: false,
      };
    }
    return data;
  },

  getBoutiqueItems: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    const query = params.toString();
    const url = query ? `${API_BASE_URL}/public/boutique-items/?${query}` : `${API_BASE_URL}/public/boutique-items/`;
    const response = await axios.get(url);
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getBoutiqueItemByRef: async (itemRef) => {
    const response = await api.get(`/public/boutique-items/${encodeURIComponent(itemRef)}/`);
    return response.data;
  },

  createBoutiqueItemWithImages: async (formData) => {
    const response = await api.post('/boutique-items/upload/', formData, { timeout: 120000 });
    return response.data;
  },

  createBoutiqueItemDraft: async (formData) => {
    const response = await api.post('/boutique-items/draft/', formData, { timeout: 60000 });
    return response.data;
  },

  uploadBoutiqueItemMedia: async (itemId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/boutique-items/${itemId}/images/`, formData, { timeout: 90000 });
    return response.data;
  },

  finalizeBoutiqueItemDraft: async (itemId) => {
    const response = await api.post(`/boutique-items/${itemId}/finalize/`, {}, { timeout: 30000 });
    return response.data;
  },

  initBoutiqueDirectUpload: async (itemId, payload) => {
    const response = await api.post(`/boutique-items/${itemId}/uploads/init/`, payload, { timeout: 30000 });
    return response.data;
  },

  signBoutiqueDirectParts: async (itemId, payload) => {
    const response = await api.post(`/boutique-items/${itemId}/uploads/sign-parts/`, payload, { timeout: 30000 });
    return response.data;
  },

  completeBoutiqueDirectUpload: async (itemId, payload) => {
    const response = await api.post(`/boutique-items/${itemId}/uploads/complete/`, payload, { timeout: 60000 });
    return response.data;
  },

  abortBoutiqueDirectUpload: async (itemId, payload) => {
    await api.post(`/boutique-items/${itemId}/uploads/abort/`, payload, { timeout: 15000 });
  },

  /** Attach media uploaded at pick time (item-less pending uploads) to a draft. */
  attachBoutiquePendingImages: async (itemId, keys) => {
    const response = await api.post(
      `/boutique-items/${itemId}/images/attach-pending/`,
      { keys },
      { timeout: 60000 },
    );
    return response.data;
  },

  getShops: async () => {
    const response = await axios.get(`${API_BASE_URL}/public/boutique-shops/`);
    return response.data;
  },

  getShopByRef: async (shopRef) => {
    const response = await axios.get(`${API_BASE_URL}/public/boutique-shops/${encodeURIComponent(shopRef)}/`);
    return response.data;
  },

  getShopReviews: async (shopRef) => {
    const response = await axios.get(`${API_BASE_URL}/public/boutique-shops/${encodeURIComponent(shopRef)}/reviews/`);
    return response.data;
  },

  addShopReview: async (shopRef, rating, comment) => {
    const response = await api.post(`/public/boutique-shops/${encodeURIComponent(shopRef)}/reviews/`, { rating, comment });
    return response.data;
  },

  getMyShop: async ({ compact } = {}) => {
    const response = await api.get('/boutique-shops/my/', {
      params: compact ? { compact: '1' } : undefined,
    });
    return response.data;
  },

  createShop: async (formData) => {
    // Let the browser set multipart Content-Type + boundary (do not set manually).
    const response = await api.post('/boutique-shops/my/', formData);
    return response.data;
  },

  updateShop: async (formData) => {
    const response = await api.put('/boutique-shops/my/', formData);
    return response.data;
  },
};
